import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import CustomPagination from "../../../utils/paginationUtils";
import editIcon from "../../../assets/edit-icon.svg";
import eyeIcon from "../../../assets/eye.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import CLOSE from "../../../assets/CLOSE.svg";
import Switch from "@mui/material/Switch";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { ReportServices } from "../../../services/ReportServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import closeIcon from "../../../assets/crossIcon.svg";
import downIcon from "../../../assets/down-icon.svg";
import { Country, State } from "country-state-city";
import { FilterDropdown } from "./FilterDropdown";
import { getUser } from "../../../config/user";

const INDIAN_STATE_LIST = State.getStatesOfCountry("IN");
const INDIAN_STATES = INDIAN_STATE_LIST.map((s) => s.name);

/** ISO state code (e.g. MH) for a selected state name or existing stateId/code */
function resolveIndianStateCode(stateId) {
  const raw = String(stateId ?? "").trim();
  if (!raw) return "";
  const byName = INDIAN_STATE_LIST.find((s) => s.name === raw);
  if (byName) return byName.isoCode;
  const byIso = INDIAN_STATE_LIST.find((s) => s.isoCode === raw);
  return byIso ? byIso.isoCode : raw;
}

function normalizeStateStatus(s) {
  const raw = String(s ?? "").trim().toLowerCase();
  return raw === "inactive" ? "inactive" : "active";
}

function stateEntryFromApi(s, userId) {
  const now = new Date().toISOString();
  if (s != null && typeof s === "object") {
    const name = s.stateId != null ? String(s.stateId).trim() : "";
    const code =
      s.stateCode != null && String(s.stateCode).trim() !== ""
        ? String(s.stateCode).trim()
        : resolveIndianStateCode(name);
    return {
      stateId: name,
      stateCode: code,
      createdAt: s.createdAt || now,
      createdBy: s.createdBy ?? userId ?? "",
      status: normalizeStateStatus(s.status),
    };
  }
  const name = String(s ?? "").trim();
  return {
    stateId: name,
    stateCode: resolveIndianStateCode(name),
    createdAt: now,
    createdBy: userId ?? "",
    status: "active",
  };
}

function newActiveStateEntry(stateName, userId) {
  const now = new Date().toISOString();
  return {
    stateId: stateName,
    stateCode: resolveIndianStateCode(stateName),
    createdAt: now,
    createdBy: userId ?? "",
    status: "active",
  };
}

function stateEntriesSnapshot(entries) {
  return [...entries]
    .map((e) => `${e.stateId}::${e.status}`)
    .sort()
    .join("|");
}

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

const PROCEDURE_COST_MAPPING_OPTIONS = [
  { value: "STATE", label: "State" },
  { value: "NATIONAL", label: "Country" },
  { value: "HOSPITAL", label: "Hospital" },
  { value: "GLOBAL", label: "Global" },
];

/** Comma-separated state codes (preferred) or stateId from API stateIds[] — not display names */
function formatStateIdsForTable(stateIds) {
  if (!Array.isArray(stateIds) || stateIds.length === 0) return "—";
  const parts = stateIds
    .map((s) => {
      if (s != null && typeof s === "object") {
        const code = s.stateCode != null && String(s.stateCode).trim() !== "" ? String(s.stateCode).trim() : null;
        const id = s.stateId != null && String(s.stateId).trim() !== "" ? String(s.stateId).trim() : null;
        return code || id || "";
      }
      const raw = String(s ?? "").trim();
      return raw;
    })
    .filter(Boolean);
  return parts.length ? parts.join(", ") : "—";
}

export default function ManageScheme() {
  const [panelVisible, setPanelVisible] = useState(false);
  const [panelType, setPanelType] = useState("add"); // "add" | "edit" | "view"
  const [currentScheme, setCurrentScheme] = useState({ name: "" });
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [users, setUsers] = useState(null); // userId -> userName map from ReportServices.getUsers()
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [schemeToInactive, setSchemeToInactive] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [initialSchemeForEdit, setInitialSchemeForEdit] = useState(null);
  /** @type {[{ stateId: string, stateCode: string, createdAt: string, createdBy: string, status: 'active'|'inactive' }]} */
  const [schemeStateEntries, setSchemeStateEntries] = useState([]);
  const [stateDropdownValue, setStateDropdownValue] = useState("");
  /** State mapping confirm: deactivate (cross on active) or activate (tick on inactive) */
  const [stateMappingModal, setStateMappingModal] = useState({
    open: false,
    action: null,
    stateName: null,
  });
  const [stateMappingBusy, setStateMappingBusy] = useState(false);
  const searchDebounceRef = useRef(null);

  const getUsersDetails = async () => {
    try {
      const response = await ReportServices.getUsers();
      setUsers(response?.data?.users ?? null);
    } catch (err) {
      handleApiErrorResponses(err);
    }
  };

  const fetchSchemes = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await ProcedureFlowServices.getDefaultSchemeTypes({
        ...(searchKey.trim() && { name: searchKey.trim() }),
        page,
        limit: PAGE_SIZE,
      });
      const apiData = res?.data;
      const list = Array.isArray(apiData?.data) ? apiData.data : [];
      setTotalCount(apiData?.total ?? list.length);
      setSchemes(
        list.map((item) => ({
          id: item.id,
          name: item.name,
          status: item.status,
          details: item.details,
          procedureCostMapping: item.procedureCostMapping,
          schemeCountryId: item.schemeCountryId,
          stateIds: item.stateIds ?? [],
          childProcedureMapping: item.childProcedureMapping ?? false,
          createdAt: item.createdAt,
          updatedAt: item.updatedAt,
          createdBy: item.createdBy,
          updatedBy: item.updatedBy,
        }))
      );
      const pages = apiData?.totalPages ?? 1;
      setTotalPages(pages > 0 ? pages : 1);
    } catch (err) {
      console.error("Failed to fetch schemes", err);
      setError("Failed to load schemes. Please try again.");
      setSchemes([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchemes();
  }, [page, searchKey]);

  useEffect(() => {
    getUsersDetails();
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchKey(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchInput]);

  const buildStateIdsPayload = (entries) =>
    entries.map((e) => ({
      stateId: e.stateId,
      stateCode: e.stateCode || resolveIndianStateCode(e.stateId),
      createdAt: e.createdAt,
      createdBy: e.createdBy,
      status: e.status,
    }));

  /** Updates current scheme stateIds from entries. Does not touch `initialSchemeForEdit` so that
   *  active/inactive changes via update-scheme-state-mapping still leave Save enabled until the user saves update-scheme-type. */
  const syncCurrentSchemeStateIdsFromEntries = (entries) => {
    const ids = buildStateIdsPayload(entries);
    Promise.resolve().then(() => {
      setCurrentScheme((prev) => ({ ...prev, stateIds: ids }));
    });
  };

  const markStateInactiveLocal = (stateName) => {
    setSchemeStateEntries((prev) => {
      const next = prev.map((e) =>
        e.stateId === stateName ? { ...e, status: "inactive" } : e
      );
      syncCurrentSchemeStateIdsFromEntries(next);
      return next;
    });
  };

  const markStateActiveLocal = (stateName) => {
    const uid = getUser()?.user?.id ?? getUser()?.id ?? "";
    const now = new Date().toISOString();
    setSchemeStateEntries((prev) => {
      const next = prev.map((e) =>
        e.stateId === stateName
          ? {
              ...e,
              status: "active",
              createdAt: e.createdAt || now,
              createdBy: e.createdBy || uid,
            }
          : e
      );
      syncCurrentSchemeStateIdsFromEntries(next);
      return next;
    });
  };

  const closeStateMappingModal = () =>
    setStateMappingModal({ open: false, action: null, stateName: null });

  const handleConfirmStateMapping = async () => {
    const stateName = stateMappingModal.stateName;
    const action = stateMappingModal.action;
    if (!stateName || !currentScheme?.id || !action) {
      closeStateMappingModal();
      return;
    }
    setStateMappingBusy(true);
    try {
      if (action === "deactivate") {
        await ProcedureFlowServices.updateSchemeStateMapping({
          schemeId: Number(currentScheme.id),
          removeStates: [stateName],
        });
        toast.success("State set to inactive.");
        markStateInactiveLocal(stateName);
      } else {
        await ProcedureFlowServices.updateSchemeStateMapping({
          schemeId: Number(currentScheme.id),
          addStates: [stateName],
        });
        toast.success("State activated.");
        markStateActiveLocal(stateName);
      }
      closeStateMappingModal();
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error(
        action === "deactivate"
          ? "Failed to update state mapping."
          : "Failed to activate state."
      );
    } finally {
      setStateMappingBusy(false);
    }
  };

  const handleSave = async () => {
    const name = (currentScheme?.name ?? "").trim();
    if (!name) {
      setSaveError("Scheme name is required.");
      return;
    }
    setSaveError(null);

    if (panelType === "add") {
      setSaving(true);
      try {
        const procedureCostMapping = currentScheme.procedureCostMapping || "STATE";
        const stateIds =
          procedureCostMapping === "STATE"
            ? buildStateIdsPayload(schemeStateEntries)
            : undefined;
        await ProcedureFlowServices.addSchemeType({
          name,
          procedureCostMapping,
          schemeCountryId: (currentScheme.schemeCountryId ?? "").trim() || "IN",
          childProcedureMapping: currentScheme.childProcedureMapping ?? false,
          ...(procedureCostMapping === "STATE" && { stateIds: stateIds ?? [] }),
        });
        setPanelVisible(false);
        setPanelType("add");
        setCurrentScheme({ name: "", procedureCostMapping: "STATE", schemeCountryId: "IN", childProcedureMapping: false });
        setSchemeStateEntries([]);
        setStateDropdownValue("");
        toast.success("Scheme added successfully.");
        await fetchSchemes();
      } catch (err) {
        console.error("Failed to add scheme", err);
        setSaveError(err?.response?.data?.message || "Failed to add scheme. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    if (panelType === "edit") {
      setSaving(true);
      try {
        const procedureCostMapping = currentScheme.procedureCostMapping || "STATE";
        const stateIds =
          procedureCostMapping === "STATE"
            ? buildStateIdsPayload(schemeStateEntries)
            : undefined;
        await ProcedureFlowServices.updateSchemeType({
          id: currentScheme.id,
          name,
          procedureCostMapping,
          schemeCountryId: (currentScheme.schemeCountryId ?? "").trim() || "IN",
          childProcedureMapping: currentScheme.childProcedureMapping ?? false,
          ...(procedureCostMapping === "STATE" && { stateIds: stateIds ?? [] }),
        });
        setPanelVisible(false);
        setPanelType("add");
        setCurrentScheme({ name: "", procedureCostMapping: "STATE", schemeCountryId: "IN", childProcedureMapping: false });
        setInitialSchemeForEdit(null);
        setSchemeStateEntries([]);
        setStateDropdownValue("");
        toast.success("Scheme updated successfully.");
        await fetchSchemes();
      } catch (err) {
        console.error("Failed to update scheme", err);
        setSaveError(err?.response?.data?.message || "Failed to update scheme. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }
  };

  const openAdd = () => {
    setPanelVisible(true);
    setPanelType("add");
    setCurrentScheme({ name: "", procedureCostMapping: "STATE", schemeCountryId: "IN", childProcedureMapping: false });
    setInitialSchemeForEdit(null);
    setSchemeStateEntries([]);
    setStateDropdownValue("");
    setSaveError(null);
  };

  const openEdit = (scheme) => {
    setPanelVisible(true);
    setPanelType("edit");
    setSaveError(null);
    const userId = getUser()?.user?.id ?? getUser()?.id ?? "";
    const stateIds = scheme.stateIds ?? [];
    const entries = Array.isArray(stateIds)
      ? stateIds.map((s) => stateEntryFromApi(s, userId))
      : [];
    setSchemeStateEntries(entries);
    setStateDropdownValue("");
    const schemeData = {
      id: scheme.id,
      name: scheme.name,
      status: scheme.status,
      details: scheme.details,
      procedureCostMapping: scheme.procedureCostMapping || "STATE",
      schemeCountryId: scheme.schemeCountryId || "IN",
      stateIds: scheme.stateIds ?? [],
      childProcedureMapping: scheme.childProcedureMapping ?? false,
      createdAt: scheme.createdAt,
      updatedAt: scheme.updatedAt,
      createdBy: scheme.createdBy,
      updatedBy: scheme.updatedBy,
    };
    setCurrentScheme(schemeData);
    setInitialSchemeForEdit(schemeData);
  };

  const openView = (scheme) => {
    setPanelVisible(true);
    setPanelType("view");
    setCurrentScheme({
      id: scheme.id,
      name: scheme.name,
      status: scheme.status,
      details: scheme.details,
      procedureCostMapping: scheme.procedureCostMapping,
      schemeCountryId: scheme.schemeCountryId,
      stateIds: scheme.stateIds ?? [],
      childProcedureMapping: scheme.childProcedureMapping ?? false,
      createdAt: scheme.createdAt,
      updatedAt: scheme.updatedAt,
      createdBy: scheme.createdBy,
      updatedBy: scheme.updatedBy,
    });
  };

  const openInactiveModal = (scheme) => {
    setSchemeToInactive(scheme);
    setInactiveModalOpen(true);
  };

  const closeInactiveModal = () => {
    setInactiveModalOpen(false);
    setSchemeToInactive(null);
  };

  const handleConfirmInactive = async () => {
    if (!schemeToInactive?.id) {
      closeInactiveModal();
      return;
    }
    try {
      await ProcedureFlowServices.updateSchemeType({
        id: String(schemeToInactive.id),
        status: "INACTIVE",
      });
      toast.success("Scheme inactivated successfully.");
      await fetchSchemes();
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error("Failed to inactivate scheme. Please try again.");
    } finally {
      closeInactiveModal();
    }
  };

  return (
    <div className="w-full min-h-screen min-h-[100dvh] flex flex-col">
      {/* Figma: Green gradient background for the entire page */}
      <div className="bg-[radial-gradient(81.98%_338.81%_at_13.41%_276.08%,_rgba(90,198,159,0.5)_0%,_rgba(222,222,222,0.5)_100%)] flex-1 min-h-full w-full pt-6 pb-3 px-3 sm:pt-8 sm:pb-4 sm:px-4 md:pt-12 md:pb-6 md:px-6">
        {/* HEADER: Title, Search, Add Button - on green background */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 sm:gap-6 py-4 sm:py-6 md:py-8 mb-4 sm:mb-6">
          <h1 className="font-lufga font-normal text-[50px] leading-[100%] tracking-[-0.07em] align-middle text-black shrink-0">
            Manage Scheme ({totalCount})
          </h1>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 flex-1 lg:flex-initial lg:max-w-[720px]">
            {/* SEARCH: capsule, white, magnifying glass, placeholder Scheme Name */}
            <div className="relative flex-1 min-w-0">
              <img
                src={searchIcon}
                alt="search"
                className="absolute left-4 sm:left-6 top-1/2 -translate-y-1/2 w-5 h-5 sm:w-6 sm:h-6 pointer-events-none"
              />
              <input
                type="text"
                placeholder="Scheme Name"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full h-12 sm:h-14 pl-11 sm:pl-14 pr-5 sm:pr-6 border border-solid border-[#E0E0E0] rounded-[54px] outline-none font-lufga text-sm sm:text-base text-[#1B1B1B] placeholder:text-gray-400 bg-white"
              />
            </div>

            {/* ADD BUTTON: Figma - 237×80, radius 54px, gap 10px, px 24, border 2px, bg #5AC69FCC */}
            <button
              onClick={openAdd}
              className="font-lufga w-[237px] h-[60px] rounded-[54px] gap-[10px] px-6 border-2 border-solid border-[#5AC69F] bg-[#5AC69FCC] flex items-center justify-center font-medium text-black hover:opacity-90 transition-opacity shrink-0"
            >
              <span className="text-xl leading-none">+</span>
              Add New Scheme
            </button>
          </div>
        </div>

        {/* MAIN AREA: White card for table */}
        <div
          className={`grid gap-6 transition-all duration-300 ${
            panelVisible ? "lg:grid-cols-[1fr_minmax(35vw,40vw)]" : "grid-cols-1"
          }`}
        >
          {/* LEFT: TABLE - White card with rounded corners and shadow */}
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-sm overflow-hidden p-4 sm:p-6 overflow-x-auto">
            {/* Table header */}
            <div className="grid grid-cols-[56px_minmax(120px,200px)_140px_minmax(88px,110px)_minmax(140px,240px)_90px_180px_1fr_120px] gap-2 px-0 sm:px-2 py-3 font-lufga font-semibold text-sm sm:text-base text-[#1B1B1B] border-b border-[#E0E0E0] min-w-[1020px]">
              <span>SNo.</span>
              <span>Scheme Name</span>
              <span>Cost Mapping Type</span>
              <span>Country</span>
              <span>State</span>
              <span>Country Code</span>
              <span>Internal Procedure mapping</span>
              <span></span>
              <span className="text-right pr-2">Actions</span>
            </div>

            <div className="font-lufga divide-y divide-[#E8E8E8]">
              {loading ? (
                <div className="py-12 text-center text-gray-500 font-lufga">
                  Loading schemes...
                </div>
              ) : error ? (
                <div className="py-12 text-center text-red-600 font-lufga">
                  {error}
                </div>
              ) : schemes.length === 0 ? (
                <div className="py-12 text-center text-gray-500 font-lufga">
                  No schemes found.
                </div>
              ) : (
                schemes.map((scheme, index) => (
                  <SchemeRow
                    key={scheme.id}
                    index={index}
                    scheme={scheme}
                    onEdit={() => openEdit(scheme)}
                    onView={() => openView(scheme)}
                    onToggleInactive={() => openInactiveModal(scheme)}
                    onReactivate={async () => {
                      try {
                        await ProcedureFlowServices.updateSchemeType({
                          id: String(scheme.id),
                          status: "ACTIVE",
                        });
                        toast.success("Scheme activated successfully.");
                        await fetchSchemes();
                      } catch (err) {
                        handleApiErrorResponses(err);
                        toast.error("Failed to activate scheme. Please try again.");
                      }
                    }}
                    alternateBg={index % 2 === 1}
                  />
                ))
              )}
            </div>

            {/* Pagination */}
            {!loading && !error && schemes.length > 0 && (
              <div className="flex justify-center mt-6 mb-4 sm:mb-6">
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                />
              </div>
            )}
          </div>

          {/* RIGHT PANEL */}
          {panelVisible && (
            <div className="bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-lufga text-[32px] font-normal">
                  {panelType === "add"
                    ? "Add Scheme"
                    : panelType === "edit"
                    ? "Edit Scheme"
                    : "View Scheme"}
                </h2>
                <img
                  src={CLOSE}
                  alt="close"
                  onClick={() => setPanelVisible(false)}
                  className="w-6 h-6 cursor-pointer"
                />
              </div>

              {(panelType === "add" || panelType === "edit") && (
                <>
                  <div className="relative mb-4">
                    <input
                      type="text"
                      placeholder="Name of the Scheme"
                      value={currentScheme?.name || ""}
                      onChange={(e) => {
                        setCurrentScheme({ ...currentScheme, name: e.target.value });
                        setSaveError(null);
                      }}
                      className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px]"
                    />
                    {(currentScheme?.name || "").length > 0 && (
                      <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                        Scheme Name
                      </span>
                    )}
                  </div>
                  <div className="relative mb-4">
                    <select
                      value={currentScheme?.procedureCostMapping || "STATE"}
                      onChange={(e) =>
                        setCurrentScheme({
                          ...currentScheme,
                          procedureCostMapping: e.target.value,
                        })
                      }
                      className="w-full h-[70px] px-6 pr-40 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
                    >
                      {PROCEDURE_COST_MAPPING_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <img
                      src={downIcon}
                      alt="down"
                      className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    />
                    <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                      Cost Mapping
                    </span>
                  </div>
                  {(currentScheme?.procedureCostMapping || "STATE") === "STATE" && (
                    <div className="mb-4">
                      <div className="w-full">
                        <FilterDropdown
                          placeholder="Select State"
                          options={INDIAN_STATES}
                          value={stateDropdownValue}
                          setValue={(state) => {
                            if (!state) return;
                            const userId = getUser()?.user?.id ?? getUser()?.id ?? "";
                            const existing = schemeStateEntries.find((e) => e.stateId === state);
                            if (existing?.status === "active") {
                              setStateDropdownValue("");
                              return;
                            }
                            if (existing?.status === "inactive") {
                              if (panelType === "edit" && currentScheme?.id) {
                                setStateMappingModal({
                                  open: true,
                                  action: "activate",
                                  stateName: state,
                                });
                              } else {
                                setSchemeStateEntries((prev) =>
                                  prev.map((e) =>
                                    e.stateId === state
                                      ? {
                                          ...e,
                                          status: "active",
                                          createdAt: e.createdAt || new Date().toISOString(),
                                          createdBy: e.createdBy || userId,
                                        }
                                      : e
                                  )
                                );
                              }
                              setStateDropdownValue("");
                              return;
                            }
                            setSchemeStateEntries((prev) => [
                              ...prev,
                              newActiveStateEntry(state, userId),
                            ]);
                            setStateDropdownValue("");
                          }}
                          enableSearch={true}
                          fullWidth={true}
                          clearValue=""
                          variant="panel"
                          keepOpenOnSelect={true}
                          selectedItems={schemeStateEntries
                            .filter((e) => e.status === "active")
                            .map((e) => e.stateId)}
                        />
                        {schemeStateEntries.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2 items-center">
                            {[...schemeStateEntries]
                              .sort((a, b) => {
                                if (a.status !== b.status)
                                  return a.status === "active" ? -1 : 1;
                                return a.stateId.localeCompare(b.stateId);
                              })
                              .map((entry) => {
                                const isActive = entry.status === "active";
                                return (
                                  <span
                                    key={entry.stateId}
                                    dir="ltr"
                                    className={`inline-flex flex-row items-center gap-2 px-3 py-1.5 rounded-full font-lufga text-xs font-medium max-w-[260px] ${
                                      isActive
                                        ? "bg-[#5AC69F]/25 text-[#1B1B1B] border border-[#5AC69F]"
                                        : "bg-gray-100 text-gray-600 border border-gray-300"
                                    }`}
                                    title={
                                      isActive
                                        ? entry.stateId
                                        : `${entry.stateId} (inactive)`
                                    }
                                  >
                                    {!isActive && (
                                      <button
                                        type="button"
                                        disabled={stateMappingBusy}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            panelType === "edit" &&
                                            currentScheme?.id
                                          ) {
                                            setStateMappingModal({
                                              open: true,
                                              action: "activate",
                                              stateName: entry.stateId,
                                            });
                                          }
                                        }}
                                        className="inline-flex shrink-0 items-center justify-center w-[22px] h-[22px] rounded-full border border-[#5AC69F] bg-white text-[#5AC69F] p-0 cursor-pointer hover:bg-[#5AC69F]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                                        title="Activate this state"
                                        aria-label={`Activate ${entry.stateId}`}
                                      >
                                        <svg
                                          width="12"
                                          height="12"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          aria-hidden
                                        >
                                          <path
                                            d="M2.5 6.2L5.1 8.8L9.5 3.5"
                                            stroke="currentColor"
                                            strokeWidth="1.4"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </button>
                                    )}
                                    <span className="min-w-0 flex-1 truncate">
                                      {entry.stateId}
                                    </span>
                                    {isActive ? (
                                      <button
                                        type="button"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (
                                            panelType === "edit" &&
                                            currentScheme?.id
                                          ) {
                                            setStateMappingModal({
                                              open: true,
                                              action: "deactivate",
                                              stateName: entry.stateId,
                                            });
                                          } else {
                                            setSchemeStateEntries((prev) =>
                                              prev.filter(
                                                (s) => s.stateId !== entry.stateId
                                              )
                                            );
                                          }
                                        }}
                                        className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#5AC69F]/80 text-[#1B1B1B] text-[10px] font-bold cursor-pointer bg-transparent border-none leading-none shrink-0"
                                      >
                                        &#x2715;
                                      </button>
                                    ) : null}
                                  </span>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <CountryDropdown
                    value={currentScheme?.schemeCountryId ?? ""}
                    onChange={(code) => {
                      setCurrentScheme({ ...currentScheme, schemeCountryId: code });
                      setSaveError(null);
                    }}
                  />
                  <div className="relative mb-4">
                    <select
                      value={currentScheme?.childProcedureMapping ? "true" : "false"}
                      onChange={(e) =>
                        setCurrentScheme({
                          ...currentScheme,
                          childProcedureMapping: e.target.value === "true",
                        })
                      }
                      className="w-full h-[70px] px-6 pr-40 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
                    >
                      <option value="false">False</option>
                      <option value="true">True</option>
                    </select>
                    <img
                      src={downIcon}
                      alt="down"
                      className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    />
                    <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
                      Internal Procedure Mapping
                    </span>
                  </div>
                  {saveError && (
                    <p className="text-red-600 text-sm font-lufga mb-4">{saveError}</p>
                  )}
                  {(() => {
                    const costMapping = currentScheme?.procedureCostMapping ?? "STATE";
                    const initialCostMapping = initialSchemeForEdit?.procedureCostMapping ?? "STATE";
                    const uidForCompare =
                      getUser()?.user?.id ?? getUser()?.id ?? "";
                    const initialEntries = Array.isArray(initialSchemeForEdit?.stateIds)
                      ? initialSchemeForEdit.stateIds.map((s) =>
                          stateEntryFromApi(s, uidForCompare)
                        )
                      : [];
                    const statesChanged =
                      costMapping === "STATE" &&
                      stateEntriesSnapshot(schemeStateEntries) !==
                        stateEntriesSnapshot(initialEntries);
                    const hasChanges =
                      panelType !== "edit" ||
                      !initialSchemeForEdit ||
                      (currentScheme?.name ?? "").trim() !== (initialSchemeForEdit?.name ?? "").trim() ||
                      costMapping !== initialCostMapping ||
                      (currentScheme?.schemeCountryId ?? "").trim() !== (initialSchemeForEdit?.schemeCountryId ?? "").trim() ||
                      Boolean(currentScheme?.childProcedureMapping) !== Boolean(initialSchemeForEdit?.childProcedureMapping) ||
                      statesChanged;
                    return (
                      <button
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                        className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {saving ? "Saving..." : panelType === "edit" ? "Save Changes" : "Add Scheme"}
                      </button>
                    );
                  })()}
                </>
              )}

              {/* VIEW: Scheme Name, Created On/By, Updated On/By */}
              {panelType === "view" && (
                <div className="font-lufga text-gray-700 space-y-3">
                  <div>
                    <strong>Scheme Name:</strong> {currentScheme?.name ?? "—"}
                  </div>
                  <div>
                    <strong>Cost Mapping Type:</strong>{" "}
                    {PROCEDURE_COST_MAPPING_OPTIONS.find((o) => o.value === currentScheme?.procedureCostMapping)?.label ??
                      currentScheme?.procedureCostMapping ??
                      "—"}
                  </div>
                  <div>
                    <strong>No of states:</strong>{" "}
                    {currentScheme?.procedureCostMapping === "STATE" &&
                    Array.isArray(currentScheme?.stateIds) &&
                    currentScheme.stateIds.length > 0
                      ? currentScheme.stateIds.length
                      : currentScheme?.procedureCostMapping === "STATE"
                        ? 0
                        : "—"}
                  </div>
                  {currentScheme?.procedureCostMapping === "STATE" && (
                    <div>
                      <strong>States:</strong>{" "}
                      {Array.isArray(currentScheme?.stateIds) && currentScheme.stateIds.length > 0
                        ? currentScheme.stateIds
                            .map((s) => {
                              const name =
                                typeof s === "object" && s?.stateId != null
                                  ? s.stateId
                                  : String(s);
                              const st =
                                typeof s === "object" && s != null && "status" in s
                                  ? normalizeStateStatus(s.status)
                                  : "active";
                              return `${name} (${st})`;
                            })
                            .filter(Boolean)
                            .join(", ") || "—"
                        : "—"}
                    </div>
                  )}
                  <div>
                    <strong>Internal Procedure Mapping:</strong>{" "}
                    {currentScheme?.childProcedureMapping ? "True" : "False"}
                  </div>
                  <div>
                    <strong>Created On:</strong>{" "}
                    {currentScheme?.createdAt
                      ? new Date(currentScheme.createdAt).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </div>
                  <div>
                    <strong>Created By:</strong>{" "}
                    {currentScheme?.createdBy
                      ? (users?.[currentScheme.createdBy] ?? currentScheme.createdBy)
                      : "—"}
                  </div>
                  <div>
                    <strong>Updated On:</strong>{" "}
                    {currentScheme?.updatedAt
                      ? new Date(currentScheme.updatedAt).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </div>
                  <div>
                    <strong>Updated By:</strong>{" "}
                    {currentScheme?.updatedBy
                      ? (users?.[currentScheme.updatedBy] ?? currentScheme.updatedBy)
                      : "—"}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {inactiveModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
            <div className="relative w-full max-w-xl bg-white rounded-3xl shadow-xl px-8 py-8 sm:px-10">
              <button
                onClick={closeInactiveModal}
                className="absolute top-6 right-6 bg-transparent border-none p-0 cursor-pointer"
              >
                <img src={closeIcon} alt="close" className="w-5 h-5" />
              </button>
              <p className="text-center font-lufga font-medium text-gray-800 text-lg mt-2 leading-snug text-balance px-1 sm:px-2">
                Do you want to inactive Scheme ?
              </p>
              <div className="flex justify-center mt-8">
                <button
                  onClick={handleConfirmInactive}
                  className="rounded-[40px] px-8 py-4 min-h-[56px] border-2 border-solid border-[#5AC69F] bg-[#5AC69F] hover:opacity-90 font-lufga font-semibold text-white transition-opacity"
                >
                  Inactive
                </button>
              </div>
            </div>
          </div>
        )}

        {stateMappingModal.open && (
          <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
            <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-xl px-8 py-10 sm:px-10 sm:py-12">
              <button
                type="button"
                onClick={closeStateMappingModal}
                disabled={stateMappingBusy}
                className="absolute top-6 right-6 bg-transparent border-none p-0 cursor-pointer disabled:opacity-50"
                aria-label="Close"
              >
                <img src={closeIcon} alt="" className="w-5 h-5" />
              </button>
              <p className="text-center font-lufga font-medium text-[#1B1B1B] text-lg sm:text-xl leading-snug mt-2 pr-8">
                {stateMappingModal.action === "activate"
                  ? "Are you sure, you want to activate the State?"
                  : "Are you sure, you want to inactive the State?"}
              </p>
              {stateMappingModal.stateName && (
                <p className="text-center font-lufga text-sm text-gray-600 mt-3">
                  {stateMappingModal.stateName}
                </p>
              )}
              <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 mt-10">
                <button
                  type="button"
                  onClick={closeStateMappingModal}
                  disabled={stateMappingBusy}
                  className="rounded-full px-8 py-3.5 min-h-[52px] border-2 border-solid border-[#E0E0E0] bg-white hover:bg-gray-50 font-lufga font-semibold text-[#1B1B1B] transition-opacity disabled:opacity-50"
                >
                  No
                </button>
                <button
                  type="button"
                  onClick={handleConfirmStateMapping}
                  disabled={stateMappingBusy}
                  className="rounded-full px-8 py-3.5 min-h-[52px] border-2 border-solid border-[#82D1B1] bg-[#82D1B1] hover:opacity-95 font-lufga font-semibold text-[#1B1B1B] transition-opacity disabled:opacity-50"
                >
                  {stateMappingBusy
                    ? "…"
                    : stateMappingModal.action === "activate"
                      ? "Yes, Activate State"
                      : "Yes, Inactive State"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ROW: SNo. | Scheme Name | Cost Mapping Type | Country Code | eye | edit | toggle */
function SchemeRow({ index, scheme, onEdit, onView, onToggleInactive, onReactivate, alternateBg }) {
  const isActive = scheme?.status === "ACTIVE";
  const [enabled, setEnabled] = useState(isActive);

  const costMappingLabel =
    PROCEDURE_COST_MAPPING_OPTIONS.find((o) => o.value === scheme?.procedureCostMapping)?.label ??
    scheme?.procedureCostMapping ??
    "-";
  const countryCode = scheme?.schemeCountryId?.trim() || "-";
  const internalProcedureMapping = scheme?.childProcedureMapping === true ? "Yes" : "No";

  useEffect(() => {
    setEnabled(isActive);
  }, [isActive]);

  const handleToggle = () => {
    if (enabled) {
      onToggleInactive?.();
    } else {
      onReactivate?.();
      setEnabled(true);
    }
  };

  const cc = scheme?.schemeCountryId?.trim();
  const countryLabel = cc ? Country.getCountryByCode(cc)?.name ?? cc : "—";
  const stateIdsDisplay = formatStateIdsForTable(scheme?.stateIds);

  return (
    <div
      className={`grid grid-cols-[56px_minmax(120px,200px)_140px_minmax(88px,110px)_minmax(140px,240px)_90px_180px_1fr_120px] gap-2 px-0 sm:px-2 py-4 sm:py-5 items-center font-lufga min-w-[1020px] ${
        alternateBg ? "bg-[#E8E8E833]" : ""
      }`}
    >
      <span className="font-medium text-sm sm:text-base text-[#1B1B1B]">
        {index + 1}
      </span>

      <p className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 leading-relaxed break-words min-w-0">
        {scheme.name}
      </p>

      <span className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 truncate">
        {costMappingLabel}
      </span>

      <span className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 truncate" title={countryLabel}>
        {countryLabel}
      </span>

      <span
        className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 break-words min-w-0 line-clamp-3"
        title={stateIdsDisplay}
      >
        {stateIdsDisplay}
      </span>

      <span className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 truncate">
        {countryCode}
      </span>

      <span className="text-sm sm:text-base text-[#1B1B1B] text-gray-800 truncate">
        {internalProcedureMapping}
      </span>

      <span />

      <div className="flex items-center gap-3 sm:gap-4 justify-end pr-2">
        <img
          src={eyeIcon}
          alt="view"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onView}
        />
        <img
          src={editIcon}
          alt="edit"
          className="w-5 h-5 cursor-pointer flex-shrink-0"
          onClick={onEdit}
        />
        <Switch
          checked={enabled}
          onChange={handleToggle}
          size="small"
          sx={{
            "& .MuiSwitch-switchBase.Mui-checked": {
              color: "#5AC69F",
              "& + .MuiSwitch-track": { backgroundColor: "#5AC69F !important", opacity: 0.6 },
            },
            "& .MuiSwitch-track": {
              backgroundColor: "#9e9e9e !important",
            },
          }}
        />
      </div>
    </div>
  );
}

function CountryDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  const countries = Country.getAllCountries();

  const filtered = search
    ? countries.filter(
        (c) =>
          c.isoCode.toLowerCase().includes(search.toLowerCase()) ||
          c.name.toLowerCase().includes(search.toLowerCase())
      )
    : countries;

  const selectedCountry = countries.find((c) => c.isoCode === value);
  const displayValue = selectedCountry
    ? `${selectedCountry.isoCode} (${selectedCountry.name})`
    : "";

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 100);
    if (!open) {
      setSearch("");
      setHighlightedIndex(-1);
    }
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={ref} className="relative mb-4">
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 w-full h-[70px] px-6 pr-36 border-2 border-solid border-gray-200 rounded-[40px] font-lufga text-[16px] bg-white cursor-pointer"
      >
        <span className={`flex-1 truncate ${displayValue ? "text-[#1B1B1B]" : "text-gray-400"}`}>
          {displayValue || "Country Code"}
        </span>
        {displayValue && (
          <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Country Code
          </span>
        )}
        <img
          src={downIcon}
          alt="down"
          className={`absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="sticky top-0 bg-white border-b border-gray-200 z-10 px-4 py-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <img src={searchIcon} alt="search" className="w-4 h-4 flex-shrink-0 opacity-50" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setHighlightedIndex(-1); }}
                onKeyDown={(e) => {
                  if (filtered.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
                  } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
                    e.preventDefault();
                    handleSelect(filtered[highlightedIndex].isoCode);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search country..."
                className="w-full border-none outline-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
              />
            </div>
          </div>
          <div ref={listRef} className="max-h-[240px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-6 py-6 text-center text-gray-500 font-lufga text-sm">
                No countries found
              </div>
            ) : (
              filtered.map((c, idx) => {
                const isSelected = c.isoCode === value;
                const isHighlighted = highlightedIndex === idx;
                return (
                  <div
                    key={c.isoCode}
                    ref={isHighlighted ? (el) => el?.scrollIntoView({ block: "nearest", behavior: "smooth" }) : undefined}
                    onClick={() => handleSelect(c.isoCode)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`px-6 py-3 cursor-pointer font-lufga text-sm border-b border-gray-100 last:border-b-0 flex items-center justify-between
                      ${isSelected
                        ? "bg-[#E8F5E9] text-[#2E7D32]"
                        : isHighlighted
                          ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]"
                          : "hover:bg-[#F4FBF8]"}`}
                  >
                    <span>
                      <span className="font-medium">{c.isoCode}</span>
                      <span className="text-gray-500 ml-2">({c.name})</span>
                    </span>
                    {isSelected && <span className="text-[#2E7D32] font-bold">✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
